import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { adminSettingsApi } from '../../../services/api'
import { Save, RefreshCw, AlertTriangle, CheckCircle, ShoppingCart, Users, Package, Search, DollarSign, Mail, Settings } from 'lucide-react'

interface MarketplaceSettings {
  general: {
    maintenanceMode: boolean
    defaultCurrency: string
    defaultLanguage: string
    productVisibility: string
  }
  vendor: {
    selfSignupAllowed: boolean
    approvalWorkflow: string
    requiredFields: string[]
    defaultRatingWeight: number
  }
  products: {
    categoriesEnabled: boolean
    featuredSlots: number
    allowAttachments: boolean
    maxAttachmentSize: number
  }
  search: {
    relevanceWeight: number
    popularityWeight: number
    vendorRatingWeight: number
    priceWeight: number
    facetedFiltersEnabled: boolean
  }
  pricing: {
    commissionPercentage: number
    transactionFee: number
    taxEnabled: boolean
    defaultTaxRate: number
  }
  promotions: {
    enabledTypes: string[]
    bannerSlotPrice: number
    featuredPrice: number
    sponsoredPrice: number
  }
  communications: {
    emailTemplates: {
      vendorApproval: string
      productPublished: string
      orderConfirmation: string
    }
    pushNotificationsEnabled: boolean
  }
  security: {
    captchaRequired: boolean
    captchaProvider: string
    virusScanEnabled: boolean
  }
  integrations: {
    enabledPaymentMethods: string[]
    webhookEndpoints: string[]
  }
  lastModified: string
  lastModifiedBy: string
}

const MarketplaceSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<MarketplaceSettings>({
    general: {
      maintenanceMode: false,
      defaultCurrency: 'USD',
      defaultLanguage: 'en',
      productVisibility: 'public'
    },
    vendor: {
      selfSignupAllowed: true,
      approvalWorkflow: 'manual',
      requiredFields: ['companyName', 'email', 'phone', 'address'],
      defaultRatingWeight: 1.0
    },
    products: {
      categoriesEnabled: true,
      featuredSlots: 10,
      allowAttachments: true,
      maxAttachmentSize: 10
    },
    search: {
      relevanceWeight: 40,
      popularityWeight: 20,
      vendorRatingWeight: 25,
      priceWeight: 15,
      facetedFiltersEnabled: true
    },
    pricing: {
      commissionPercentage: 5,
      transactionFee: 2.50,
      taxEnabled: true,
      defaultTaxRate: 8.5
    },
    promotions: {
      enabledTypes: ['banner', 'featured', 'sponsored'],
      bannerSlotPrice: 49.99,
      featuredPrice: 29.99,
      sponsoredPrice: 99.99
    },
    communications: {
      emailTemplates: {
        vendorApproval: 'Welcome {{vendorName}}! Your vendor account has been approved.',
        productPublished: 'Your product "{{productName}}" is now live!',
        orderConfirmation: 'Order #{{orderId}} confirmed. Thank you for your purchase!'
      },
      pushNotificationsEnabled: true
    },
    security: {
      captchaRequired: true,
      captchaProvider: 'reCAPTCHA',
      virusScanEnabled: true
    },
    integrations: {
      enabledPaymentMethods: ['Stripe', 'Wire'],
      webhookEndpoints: ['https://api.example.com/webhooks/orders']
    },
    lastModified: new Date().toISOString(),
    lastModifiedBy: 'admin@evoltbay.com'
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [authError, setAuthError] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await adminSettingsApi.getSettings('marketplace')
      if (response.success) {
        setSettings(response.data)
      } else {
        console.warn('API returned success=false:', response)
        // Keep default settings on API error
      }
    } catch (error: any) {
      console.error('Failed to load marketplace settings:', error)

      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Authentication error - user may not be logged in as admin')
        setAuthError(true)
      }

      // Keep default settings on error so UI doesn't crash
    } finally {
      setLoading(false)
    }
  }

  const handleSettingChange = (path: string, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev }
      const keys = path.split('.')
      let current: any = newSettings

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      current[keys[keys.length - 1]] = value

      return newSettings
    })
    setHasChanges(true)
  }

  const handleArrayToggle = (path: string, value: string) => {
    setSettings(prev => {
      const newSettings = { ...prev }
      const keys = path.split('.')
      let current: any = newSettings

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }

      const array = current[keys[keys.length - 1]]
      if (array.includes(value)) {
        current[keys[keys.length - 1]] = array.filter((item: string) => item !== value)
      } else {
        current[keys[keys.length - 1]] = [...array, value]
      }

      return newSettings
    })
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const response = await adminSettingsApi.updateSettings('marketplace', settings)
      if (response.success) {
        setSettings(response.data)
        setHasChanges(false)
        toast.success('Marketplace settings saved successfully!')
      }
    } catch (error) {
      console.error('Failed to save marketplace settings:', error)
      toast.error('Failed to save marketplace settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    try {
      setSaving(true)
      const response = await adminSettingsApi.resetSettings('marketplace')
      if (response.success) {
        setSettings(response.data)
        setHasChanges(false)
        toast.success('Marketplace settings reset to defaults!')
      }
    } catch (error) {
      console.error('Failed to reset marketplace settings:', error)
      toast.error('Failed to reset marketplace settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-gray-600">Loading marketplace settings...</p>
      </div>
    )
  }

  if (authError) {
    return (
      <div className="py-12 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-yellow-600" />
          </div>
          <h3 className="text-lg font-medium text-yellow-800 mb-2">
            Authentication Required
          </h3>
          <p className="text-yellow-700 mb-4">
            You need to be logged in as an admin to access these settings.
          </p>
          <p className="text-sm text-yellow-600">
            Please log in with admin credentials and try again.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 flex-shrink-0" />
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Marketplace Settings</h2>
            <p className="text-gray-600 text-sm sm:text-base touch-manipulation">Configure products, vendors, pricing, and marketplace operations</p>
          </div>
        </div>

        {/* Save/Reset Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!hasChanges}
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="text-sm sm:text-base touch-manipulation">{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Last Modified Info */}
      <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <span className="text-sm text-gray-600">
          Last modified: {new Date(settings.lastModified).toLocaleString()} by {settings.lastModifiedBy}
        </span>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
        {/* General Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2 text-gray-600" />
            General
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
                <p className="text-xs text-gray-500">Put marketplace in maintenance mode</p>
              </div>
              <input
                type="checkbox"
                checked={settings.general?.maintenanceMode}
                onChange={(e) => handleSettingChange('general.maintenanceMode', e.target.checked)}
                className="mobile-checkbox text-blue-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Currency</label>
              <select
                value={settings.general?.defaultCurrency}
                onChange={(e) => handleSettingChange('general.defaultCurrency', e.target.value)}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="CAD">CAD (C$)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Language</label>
              <select
                value={settings.general?.defaultLanguage}
                onChange={(e) => handleSettingChange('general.defaultLanguage', e.target.value)}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Visibility</label>
              <select
                value={settings.general?.productVisibility}
                onChange={(e) => handleSettingChange('general.productVisibility', e.target.value)}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="vetting-only">Vetting Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Vendor Management */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-purple-600" />
            Vendor Management
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Self-Signup Allowed</label>
                <p className="text-xs text-gray-500">Allow vendors to register themselves</p>
              </div>
              <input
                type="checkbox"
                checked={settings.vendor?.selfSignupAllowed}
                onChange={(e) => handleSettingChange('vendor.selfSignupAllowed', e.target.checked)}
                className="h-4 w-4 text-purple-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Approval Workflow</label>
              <select
                value={settings.vendor?.approvalWorkflow}
                onChange={(e) => handleSettingChange('vendor.approvalWorkflow', e.target.value)}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="auto-approve">Auto-Approve</option>
                <option value="manual">Manual Review</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Required Fields</label>
              <div className="space-y-2">
                {['companyName', 'email', 'phone', 'address', 'taxId', 'license'].map(field => (
                  <label key={field} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.vendor?.requiredFields.includes(field)}
                      onChange={() => handleArrayToggle('vendor.requiredFields', field)}
                      className="h-4 w-4 text-purple-600 rounded"
                    />
                    <span className="text-sm capitalize">{field.replace(/([A-Z])/g, ' $1')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Rating Weight</label>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={settings.vendor?.defaultRatingWeight}
                onChange={(e) => handleSettingChange('vendor.defaultRatingWeight', parseFloat(e.target.value))}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Products & Catalog */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Package className="h-5 w-5 mr-2 text-green-600" />
            Products & Catalog
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Categories Enabled</label>
                <p className="text-xs text-gray-500">Allow product categorization</p>
              </div>
              <input
                type="checkbox"
                checked={settings.products?.categoriesEnabled}
                onChange={(e) => handleSettingChange('products.categoriesEnabled', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Featured Product Slots</label>
              <input
                type="number"
                min="0"
                max="50"
                value={settings.products?.featuredSlots}
                onChange={(e) => handleSettingChange('products.featuredSlots', parseInt(e.target.value))}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Attachments</label>
                <p className="text-xs text-gray-500">Allow file attachments on products</p>
              </div>
              <input
                type="checkbox"
                checked={settings.products?.allowAttachments}
                onChange={(e) => handleSettingChange('products.allowAttachments', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            {settings.products?.allowAttachments && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Attachment Size (MB)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={settings.products?.maxAttachmentSize}
                  onChange={(e) => handleSettingChange('products.maxAttachmentSize', parseInt(e.target.value))}
                  className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Search & Ranking */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Search className="h-5 w-5 mr-2 text-orange-600" />
            Search & Ranking
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Weighting (%)</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Relevance</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.search?.relevanceWeight || 40}
                    onChange={(e) => handleSettingChange('search.relevanceWeight', parseInt(e.target.value))}
                    className="w-full px-3 py-2 sm:py-3 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm sm:text-base touch-manipulation touch-manipulation"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Popularity</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.search?.popularityWeight || 20}
                    onChange={(e) => handleSettingChange('search.popularityWeight', parseInt(e.target.value))}
                    className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Vendor Rating</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.search?.vendorRatingWeight || 25}
                    onChange={(e) => handleSettingChange('search.vendorRatingWeight', parseInt(e.target.value))}
                    className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Price</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.search?.priceWeight || 15}
                    onChange={(e) => handleSettingChange('search.priceWeight', parseInt(e.target.value))}
                    className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Faceted Filters</label>
                <p className="text-xs text-gray-500">Enable category, price, and attribute filters</p>
              </div>
              <input
                type="checkbox"
                checked={settings.search?.facetedFiltersEnabled || false}
                onChange={(e) => handleSettingChange('search.facetedFiltersEnabled', e.target.checked)}
                className="h-4 w-4 text-orange-600 rounded"
              />
            </div>
          </div>
        </div>

        {/* Pricing & Fees */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-green-600" />
            Pricing & Fees
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Commission Percentage</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.pricing?.commissionPercentage}
                onChange={(e) => handleSettingChange('pricing.commissionPercentage', parseFloat(e.target.value))}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Fee ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={settings.pricing?.transactionFee}
                onChange={(e) => handleSettingChange('pricing.transactionFee', parseFloat(e.target.value))}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Tax Handling</label>
                <p className="text-xs text-gray-500">Enable automatic tax calculation</p>
              </div>
              <input
                type="checkbox"
                checked={settings.pricing?.taxEnabled}
                onChange={(e) => handleSettingChange('pricing.taxEnabled', e.target.checked)}
                className="h-4 w-4 text-green-600 rounded"
              />
            </div>

            {settings.pricing?.taxEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Tax Rate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  step="0.1"
                  value={settings.pricing?.defaultTaxRate}
                  onChange={(e) => handleSettingChange('pricing.defaultTaxRate', parseFloat(e.target.value))}
                  className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Communications */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Mail className="h-5 w-5 mr-2 text-blue-600" />
            Communications
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Push Notifications</label>
                <p className="text-xs text-gray-500">Enable push notifications for new orders</p>
              </div>
              <input
                type="checkbox"
                checked={settings.communications?.pushNotificationsEnabled || false}
                onChange={(e) => handleSettingChange('communications.pushNotificationsEnabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Approval Email Template</label>
              <textarea
                rows={3}
                value={settings.communications?.emailTemplates?.vendorApproval || ''}
                onChange={(e) => handleSettingChange('communications.emailTemplates.vendorApproval', e.target.value)}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Welcome {{vendorName}}! Your vendor account has been approved."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Published Email Template</label>
              <textarea
                rows={3}
                value={settings.communications?.emailTemplates?.productPublished || ''}
                onChange={(e) => handleSettingChange('communications.emailTemplates.productPublished', e.target.value)}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Your product {{productName}} is now live!"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Confirmation Email Template</label>
              <textarea
                rows={3}
                value={settings.communications?.emailTemplates?.orderConfirmation || ''}
                onChange={(e) => handleSettingChange('communications.emailTemplates.orderConfirmation', e.target.value)}
                className="mobile-input border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Order #{{orderId}} confirmed. Thank you for your purchase!"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="text-yellow-800 font-medium">Unsaved Changes</p>
              <p className="text-yellow-700 text-sm">You have unsaved changes. Click "Save Changes" to apply them.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketplaceSettingsPanel
